import { useState, useRef, useEffect } from 'react';

export interface UrlMeta {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
  url?: string;
  resolvedUrl?: string;
  resolvedFinalUrl?: string;
  partner?: string;
  degraded?: boolean;
  embed?: any;
  error?: boolean;
  message?: string;
  status?: number;
  body?: string;
}

export const useUrlParser = () => {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<UrlMeta | null>(null);
  const extractMetaWatchdog = useRef<number | null>(null);
  const loadingRef = useRef<boolean>(false);

  // Helper: try fetching from noembed (CORS-friendly oEmbed proxy) for better YouTube metadata
  const tryNoembed = async (url: string) => {
    try {
      const res = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(url)}`);
      if (res.ok) {
        const data = await res.json();
        if (data && !data.error) {
          return {
            title: data.title,
            description: data.author_name ? `By ${data.author_name}` : undefined,
            siteName: data.provider_name || 'YouTube',
            image: data.thumbnail_url,
            favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
            url: url,
            resolvedUrl: url,
            // noembed returns html for embedding, we can use it
            embed: data.html ? { kind: 'html', html: data.html } : undefined
          };
        }
      }
    } catch (e) {
      console.warn('[useUrlParser] noembed fetch failed', e);
    }
    return null;
  };

  const detectYouTubeEmbed = (url: string) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      const id = match[2];
      return {
        kind: 'youtube',
        id: id,
        src: `https://www.youtube.com/embed/${id}`,
        poster: `https://img.youtube.com/vi/${id}/hqdefault.jpg`
      };
    }
    return null;
  };

  const extractMeta = async (targetUrl: string) => {
    if (!targetUrl || loading) return;
    setLoading(true);
    loadingRef.current = true;
    setMeta(null);

    try {
      const endpoint = 'https://extract-meta.cubemeet.app/api/extract-meta?url=' + encodeURIComponent(targetUrl.trim());
      console.debug('[useUrlParser] extractMeta -> requesting', { endpoint, targetUrl });
      
      // watchdog: if fetch doesn't finish in reasonable time, force-reset loading state
      try {
        if (extractMetaWatchdog.current) window.clearTimeout(extractMetaWatchdog.current);
      } catch {}
      extractMetaWatchdog.current = window.setTimeout(() => {
        if (loadingRef.current) {
          console.warn('[useUrlParser] extractMeta -> watchdog timeout, resetting loading state');
          loadingRef.current = false;
          try { setLoading(false); } catch (e) {}
        }
      }, 10000);

      const res = await fetch(endpoint, { method: 'GET', headers: { 'Accept': 'application/json' } });
      console.debug('[useUrlParser] extractMeta -> response', { status: res.status, ok: res.ok });
      
      let success = false;
      if (res.ok) {
        const json = await res.json();
        console.debug('[useUrlParser] extractMeta -> json', json && (typeof json === 'object' ? { keys: Object.keys(json).slice(0,10) } : String(json)));
        
        // Check if result is too generic for YouTube (e.g. title is just "YouTube")
        const isGenericYoutube = (json.siteName === 'YouTube' || json.siteName === 'Google') && (json.title === 'YouTube' || json.title === '- YouTube' || !json.title);
        
        if (isGenericYoutube) {
             const yt = detectYouTubeEmbed(targetUrl);
             if (yt) {
                 const enhanced = await tryNoembed(targetUrl);
                 if (enhanced) {
                     setMeta(enhanced);
                     success = true;
                 } else {
                     // Fallback to local if noembed fails but we have video ID
                     setMeta({
                        ...json,
                        title: 'YouTube Video',
                        image: yt.poster || json.image,
                     });
                     success = true;
                 }
             } else {
                 setMeta(json);
                 success = true;
             }
        } else {
            setMeta(json);
            success = true;
        }
      } 
      
      if (!success) {
        const yt = detectYouTubeEmbed(targetUrl);
        if (yt) {
          // Try noembed first as fallback
          const enhanced = await tryNoembed(targetUrl);
          if (enhanced) {
             setMeta(enhanced);
          } else {
             setMeta({
                title: 'YouTube Video',
                description: 'Watch on YouTube',
                siteName: 'YouTube',
                image: yt.poster,
                favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
                url: targetUrl,
                resolvedUrl: targetUrl
              });
          }
        } else {
          let text = undefined;
          try { text = await res.text(); } catch (e) {}
          console.warn('[useUrlParser] extractMeta -> non-ok response', { status: res.status, body: text });
          setMeta({ error: true, status: res.status, body: text });
        }
      }
    } catch (e) {
      console.error('[useUrlParser] extractMeta -> fetch error', e);
      const yt = detectYouTubeEmbed(targetUrl);
      if (yt) {
        // Try noembed first as fallback
        const enhanced = await tryNoembed(targetUrl);
        if (enhanced) {
            setMeta(enhanced);
        } else {
            setMeta({
              title: 'YouTube Video',
              description: 'Watch on YouTube',
              siteName: 'YouTube',
              image: yt.poster,
              favicon: 'https://www.google.com/s2/favicons?domain=youtube.com&sz=64',
              url: targetUrl,
              resolvedUrl: targetUrl
            });
        }
      } else {
        setMeta({ error: true, message: String(e) });
      }
    } finally {
      try {
        if (extractMetaWatchdog.current) { window.clearTimeout(extractMetaWatchdog.current); extractMetaWatchdog.current = null; }
      } catch {}
      loadingRef.current = false;
      setLoading(false);
    }
  };

  return {
    loading,
    meta,
    extractMeta,
    setMeta
  };
};
